import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Upload, Trash2, Send as SendIcon } from "lucide-react";
import { api, API_BASE, TOKEN_KEY, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { COMMON_CURRENCIES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BillingTab from "@/pages/BillingTab";

export default function Settings() {
  const { t } = useTranslation();
  const { company, setCompany } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/company");
    setForm({
      name: data.name,
      address: {
        street: data.address?.street || "",
        postal_code: data.address?.postal_code || "",
        city: data.address?.city || "",
        country: data.address?.country || "",
      },
      default_currency: data.default_currency,
      default_language: data.default_language,
      invoice_number_prefix: data.invoice_number_prefix || "INV-",
      email_settings: {
        from_name: data.email_settings?.from_name || "",
        from_email: data.email_settings?.from_email || "",
        smtp_host: data.email_settings?.smtp_host || "",
        smtp_port: data.email_settings?.smtp_port || 587,
        smtp_username: data.email_settings?.smtp_username || "",
        smtp_password: "",
        smtp_use_tls: data.email_settings?.smtp_use_tls ?? true,
        enabled: !!data.email_settings?.enabled,
        password_set: !!data.email_settings?.password_set,
      },
      invoice_defaults: {
        payment_terms_days: data.invoice_defaults?.payment_terms_days ?? 30,
        footer_en: data.invoice_defaults?.footer_en || "",
        footer_ar: data.invoice_defaults?.footer_ar || "",
        footer_sv: data.invoice_defaults?.footer_sv || "",
        notes_en: data.invoice_defaults?.notes_en || "",
        notes_ar: data.invoice_defaults?.notes_ar || "",
        notes_sv: data.invoice_defaults?.notes_sv || "",
      },
      reminder_settings: {
        enabled: data.reminder_settings?.enabled ?? true,
        before_due_days: data.reminder_settings?.before_due_days ?? 3,
        on_due: data.reminder_settings?.on_due ?? true,
        after_due_days: data.reminder_settings?.after_due_days ?? 7,
      },
      stripe_settings: {
        enabled: !!data.stripe_settings?.enabled,
        publishable_key: data.stripe_settings?.publishable_key || "",
        secret_key: "",
        webhook_secret: "",
        secret_key_set: !!data.stripe_settings?.secret_key_set,
        webhook_secret_set: !!data.stripe_settings?.webhook_secret_set,
      },
      payment_info: {
        org_nr: data.payment_info?.org_nr || "",
        vat_reg_no: data.payment_info?.vat_reg_no || "",
        f_skatt_approved: !!data.payment_info?.f_skatt_approved,
        bankgiro: data.payment_info?.bankgiro || "",
        plusgiro: data.payment_info?.plusgiro || "",
        swish_number: data.payment_info?.swish_number || "",
        iban: data.payment_info?.iban || "",
        bic: data.payment_info?.bic || "",
        late_payment_interest_note_sv:
          data.payment_info?.late_payment_interest_note_sv || "",
        late_payment_interest_note_en:
          data.payment_info?.late_payment_interest_note_en || "",
        late_payment_interest_note_ar:
          data.payment_info?.late_payment_interest_note_ar || "",
      },
      logo_url: data.logo_url || null,
    });
    setCompany(data);
  }, [setCompany]);

  useEffect(() => {
    load();
  }, [load]);

  if (!form) {
    return <div className="text-sm text-slate-500">{t("common.loading")}</div>;
  }

  function set(group, field, value) {
    setForm((f) => ({ ...f, [group]: { ...f[group], [field]: value } }));
  }

  async function saveCompanyTab() {
    setSaving(true);
    try {
      const { data } = await api.patch("/company", {
        name: form.name,
        address: {
          street: form.address.street || null,
          postal_code: form.address.postal_code || null,
          city: form.address.city || null,
          country: form.address.country || null,
        },
        default_currency: form.default_currency,
        default_language: form.default_language,
        invoice_number_prefix: form.invoice_number_prefix,
      });
      setCompany(data);
      toast.success(t("settings.toasts.saved"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  async function saveInvoicingTab() {
    setSaving(true);
    try {
      await api.patch("/company", { invoice_defaults: form.invoice_defaults });
      toast.success(t("settings.toasts.saved"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  async function saveEmailTab() {
    setSaving(true);
    try {
      const es = { ...form.email_settings };
      // only send password if user typed one
      if (!es.smtp_password) delete es.smtp_password;
      delete es.password_set;
      const { data } = await api.patch("/company", { email_settings: es });
      setCompany(data);
      // Blank password field after save
      set("email_settings", "smtp_password", "");
      toast.success(t("settings.toasts.saved"));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  async function saveRemindersTab() {
    setSaving(true);
    try {
      await api.patch("/company", { reminder_settings: form.reminder_settings });
      toast.success(t("settings.toasts.saved"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  async function saveStripeTab() {
    setSaving(true);
    try {
      const ss = { ...form.stripe_settings };
      if (!ss.secret_key) delete ss.secret_key;
      if (!ss.webhook_secret) delete ss.webhook_secret;
      delete ss.secret_key_set;
      delete ss.webhook_secret_set;
      await api.patch("/company", { stripe_settings: ss });
      set("stripe_settings", "secret_key", "");
      set("stripe_settings", "webhook_secret", "");
      toast.success(t("settings.toasts.saved"));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  async function savePaymentTab() {
    setSaving(true);
    try {
      const pi = { ...form.payment_info };
      await api.patch("/company", { payment_info: pi });
      toast.success(t("settings.toasts.saved"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  async function sendTestEmail() {
    setTestEmailResult(null);
    try {
      const { data } = await api.post("/admin/test-email", {});
      setTestEmailResult(data);
      if (data.status === "sent") toast.success(t("settings.email.testSent"));
      else if (data.status === "mocked") toast.message(t("settings.email.testMocked"));
      else toast.error(t("settings.email.testFailed"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    }
  }

  async function uploadLogo(file) {
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem(TOKEN_KEY);
      const res = await fetch(`${API_BASE}/company/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setCompany(data);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success(t("settings.company.logoUpdated"));
    } catch (err) {
      toast.error(err.message || t("settings.toasts.error"));
    } finally {
      setLogoBusy(false);
    }
  }

  async function removeLogo() {
    setLogoBusy(true);
    try {
      const { data } = await api.delete("/company/logo");
      setCompany(data);
      setForm((f) => ({ ...f, logo_url: null }));
      toast.success(t("settings.company.logoRemoved"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setLogoBusy(false);
    }
  }

  const stripeCanEnable =
    !!form.stripe_settings.publishable_key &&
    (!!form.stripe_settings.secret_key || form.stripe_settings.secret_key_set) &&
    (!!form.stripe_settings.webhook_secret || form.stripe_settings.webhook_secret_set);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header className="space-y-2">
        <h1 className="font-heading text-4xl tracking-tight text-slate-900">
          {t("settings.title")}
        </h1>
        <p className="max-w-xl text-sm leading-relaxed text-slate-500">
          {t("settings.subtitle", { company: company?.name })}
        </p>
      </header>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="h-auto flex-wrap gap-1 bg-slate-100">
          <TabsTrigger value="company" data-testid="tab-company">{t("settings.tabs.company")}</TabsTrigger>
          <TabsTrigger value="invoicing" data-testid="tab-invoicing">{t("settings.tabs.invoicing")}</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">{t("settings.tabs.email")}</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">{t("settings.tabs.reminders")}</TabsTrigger>
          <TabsTrigger value="stripe" data-testid="tab-stripe">{t("settings.tabs.stripe")}</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">{t("settings.tabs.billing", "Billing")}</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment">{t("settings.tabs.payment")}</TabsTrigger>
          <TabsTrigger value="accounting" data-testid="tab-accounting">{t("settings.tabs.accounting")}</TabsTrigger>
        </TabsList>

        {/* COMPANY */}
        <TabsContent value="company" className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <h2 className="font-heading text-lg">{t("settings.company.title")}</h2>

            <div className="flex items-start gap-4">
              <div
                className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-50"
                data-testid="settings-logo-preview"
              >
                {form.logo_url ? (
                  <img
                    src={`${API_BASE.replace(/\/api$/, "")}${form.logo_url}?v=${Date.now()}`}
                    alt="logo"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <Mail className="h-8 w-8 text-slate-300" />
                )}
              </div>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={logoBusy}
                    data-testid="settings-logo-upload"
                    className="gap-1"
                  >
                    <Upload className="h-4 w-4" />
                    {t("settings.company.uploadLogo")}
                  </Button>
                  {form.logo_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={removeLogo}
                      disabled={logoBusy}
                      data-testid="settings-logo-remove"
                      className="gap-1 text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("common.delete")}
                    </Button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
                <div className="text-xs text-slate-500">{t("settings.company.logoHint")}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.company.name")}</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="settings-company-name"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.company.prefix")}</Label>
                <Input
                  value={form.invoice_number_prefix}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, invoice_number_prefix: e.target.value }))
                  }
                  data-testid="settings-company-prefix"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>{t("settings.company.address")}</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    value={form.address.street}
                    onChange={(e) => set("address", "street", e.target.value)}
                    placeholder={t("settings.company.street")}
                    data-testid="settings-company-street"
                    className="sm:col-span-2"
                  />
                  <Input
                    value={form.address.postal_code}
                    onChange={(e) => set("address", "postal_code", e.target.value)}
                    placeholder={t("settings.company.postalCode")}
                    data-testid="settings-company-postal-code"
                  />
                  <Input
                    value={form.address.city}
                    onChange={(e) => set("address", "city", e.target.value)}
                    placeholder={t("settings.company.city")}
                    data-testid="settings-company-city"
                  />
                  <Input
                    value={form.address.country}
                    onChange={(e) => set("address", "country", e.target.value)}
                    placeholder={t("settings.company.country")}
                    data-testid="settings-company-country"
                    className="sm:col-span-2"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("settings.company.currency")}</Label>
                <Select
                  value={form.default_currency}
                  onValueChange={(v) => setForm((f) => ({ ...f, default_currency: v }))}
                >
                  <SelectTrigger data-testid="settings-company-currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("settings.company.language")}</Label>
                <Select
                  value={form.default_language}
                  onValueChange={(v) => setForm((f) => ({ ...f, default_language: v }))}
                >
                  <SelectTrigger data-testid="settings-company-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ar">العربية</SelectItem>
                    <SelectItem value="sv">Svenska</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={saveCompanyTab}
                disabled={saving}
                data-testid="settings-company-save"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("common.save")}
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* INVOICING DEFAULTS */}
        <TabsContent value="invoicing" className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <h2 className="font-heading text-lg">{t("settings.invoicing.title")}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.invoicing.paymentTerms")}</Label>
                <Input
                  type="number"
                  min={0}
                  max={365}
                  value={form.invoice_defaults.payment_terms_days}
                  onChange={(e) =>
                    set("invoice_defaults", "payment_terms_days", Number(e.target.value))
                  }
                  data-testid="settings-invoicing-terms"
                />
              </div>
            </div>
            {["en", "ar", "sv"].map((lng) => (
              <div key={lng} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("settings.invoicing.footer")} — {lng.toUpperCase()}</Label>
                  <Textarea
                    rows={2}
                    dir={lng === "ar" ? "rtl" : "ltr"}
                    value={form.invoice_defaults[`footer_${lng}`]}
                    onChange={(e) =>
                      set("invoice_defaults", `footer_${lng}`, e.target.value)
                    }
                    data-testid={`settings-invoicing-footer-${lng}`}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.invoicing.notes")} — {lng.toUpperCase()}</Label>
                  <Textarea
                    rows={2}
                    dir={lng === "ar" ? "rtl" : "ltr"}
                    value={form.invoice_defaults[`notes_${lng}`]}
                    onChange={(e) =>
                      set("invoice_defaults", `notes_${lng}`, e.target.value)
                    }
                    data-testid={`settings-invoicing-notes-${lng}`}
                  />
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button
                onClick={saveInvoicingTab}
                disabled={saving}
                data-testid="settings-invoicing-save"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("common.save")}
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* EMAIL */}
        <TabsContent value="email" className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <h2 className="font-heading text-lg">{t("settings.email.title")}</h2>
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <Label className="cursor-pointer">{t("settings.email.enabled")}</Label>
              <Switch
                checked={form.email_settings.enabled}
                onCheckedChange={(v) => set("email_settings", "enabled", v)}
                data-testid="settings-email-enabled"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.email.fromName")}</Label>
                <Input
                  value={form.email_settings.from_name}
                  onChange={(e) => set("email_settings", "from_name", e.target.value)}
                  data-testid="settings-email-fromname"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.email.fromEmail")}</Label>
                <Input
                  type="email"
                  value={form.email_settings.from_email}
                  onChange={(e) => set("email_settings", "from_email", e.target.value)}
                  data-testid="settings-email-fromemail"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.email.host")}</Label>
                <Input
                  value={form.email_settings.smtp_host}
                  onChange={(e) => set("email_settings", "smtp_host", e.target.value)}
                  data-testid="settings-email-host"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.email.port")}</Label>
                <Input
                  type="number"
                  value={form.email_settings.smtp_port}
                  onChange={(e) => set("email_settings", "smtp_port", Number(e.target.value))}
                  data-testid="settings-email-port"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.email.username")}</Label>
                <Input
                  value={form.email_settings.smtp_username}
                  onChange={(e) => set("email_settings", "smtp_username", e.target.value)}
                  data-testid="settings-email-username"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  {t("settings.email.password")}
                  {form.email_settings.password_set && (
                    <Badge className="ms-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                      {t("settings.email.passwordSet")}
                    </Badge>
                  )}
                </Label>
                <Input
                  type="password"
                  placeholder={
                    form.email_settings.password_set
                      ? t("settings.email.passwordKeep")
                      : t("settings.email.passwordNew")
                  }
                  value={form.email_settings.smtp_password}
                  onChange={(e) => set("email_settings", "smtp_password", e.target.value)}
                  data-testid="settings-email-password"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 sm:col-span-2">
                <Label className="cursor-pointer">{t("settings.email.tls")}</Label>
                <Switch
                  checked={form.email_settings.smtp_use_tls}
                  onCheckedChange={(v) => set("email_settings", "smtp_use_tls", v)}
                  data-testid="settings-email-tls"
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={sendTestEmail}
                data-testid="settings-email-test"
                className="gap-1"
              >
                <SendIcon className="h-4 w-4 rtl:-scale-x-100" />
                {t("settings.email.testButton")}
              </Button>
              <Button
                onClick={saveEmailTab}
                disabled={saving}
                data-testid="settings-email-save"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("common.save")}
              </Button>
            </div>

            {testEmailResult && (
              <div
                className={`rounded-md border p-3 text-sm ${
                  testEmailResult.status === "sent"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : testEmailResult.status === "mocked"
                      ? "border-amber-200 bg-amber-50 text-amber-800"
                      : "border-red-200 bg-red-50 text-red-800"
                }`}
                data-testid="settings-email-test-result"
              >
                <div className="font-medium uppercase text-xs tracking-wide">
                  {testEmailResult.status}
                </div>
                <div className="mt-1">to {testEmailResult.to_email}</div>
                {testEmailResult.error && (
                  <div className="mt-1 text-xs font-mono whitespace-pre-wrap">
                    {testEmailResult.error}
                  </div>
                )}
              </div>
            )}
          </section>
        </TabsContent>

        {/* REMINDERS */}
        <TabsContent value="reminders" className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <h2 className="font-heading text-lg">{t("settings.reminders.title")}</h2>
            <p className="text-sm text-slate-500">{t("settings.reminders.description")}</p>
            <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <Label className="cursor-pointer">{t("settings.reminders.enabled")}</Label>
              <Switch
                checked={form.reminder_settings.enabled}
                onCheckedChange={(v) => set("reminder_settings", "enabled", v)}
                data-testid="settings-reminders-enabled"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label>{t("settings.reminders.beforeDue")}</Label>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={form.reminder_settings.before_due_days}
                  onChange={(e) =>
                    set("reminder_settings", "before_due_days", Number(e.target.value))
                  }
                  data-testid="settings-reminders-before"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <Label className="cursor-pointer">{t("settings.reminders.onDue")}</Label>
                <Switch
                  checked={form.reminder_settings.on_due}
                  onCheckedChange={(v) => set("reminder_settings", "on_due", v)}
                  data-testid="settings-reminders-ondue"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.reminders.afterDue")}</Label>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={form.reminder_settings.after_due_days}
                  onChange={(e) =>
                    set("reminder_settings", "after_due_days", Number(e.target.value))
                  }
                  data-testid="settings-reminders-after"
                />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/run-reminders");
                    toast.success(
                      t("settings.reminders.runResult", {
                        processed: data.processed,
                        sent: data.sent_reminders,
                        skipped: data.skipped_already_sent,
                      })
                    );
                  } catch (err) {
                    toast.error(formatApiError(err, t("settings.toasts.error")));
                  }
                }}
                data-testid="settings-reminders-run"
              >
                {t("settings.reminders.runNow")}
              </Button>
              <Button
                onClick={saveRemindersTab}
                disabled={saving}
                data-testid="settings-reminders-save"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("common.save")}
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* STRIPE */}
        <TabsContent value="stripe" className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <h2 className="font-heading text-lg">{t("settings.stripe.title")}</h2>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {t("settings.stripe.hint")}
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>{t("settings.stripe.publishable")}</Label>
                <Input
                  value={form.stripe_settings.publishable_key}
                  onChange={(e) => set("stripe_settings", "publishable_key", e.target.value)}
                  placeholder="pk_test_..."
                  data-testid="settings-stripe-publishable"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  {t("settings.stripe.secret")}
                  {form.stripe_settings.secret_key_set && (
                    <Badge className="ms-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                      {t("settings.email.passwordSet")}
                    </Badge>
                  )}
                </Label>
                <Input
                  type="password"
                  placeholder={
                    form.stripe_settings.secret_key_set
                      ? t("settings.email.passwordKeep")
                      : "sk_test_..."
                  }
                  value={form.stripe_settings.secret_key}
                  onChange={(e) => set("stripe_settings", "secret_key", e.target.value)}
                  data-testid="settings-stripe-secret"
                />
              </div>
              <div className="space-y-2">
                <Label>
                  {t("settings.stripe.webhook")}
                  {form.stripe_settings.webhook_secret_set && (
                    <Badge className="ms-2 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                      {t("settings.email.passwordSet")}
                    </Badge>
                  )}
                </Label>
                <Input
                  type="password"
                  placeholder={
                    form.stripe_settings.webhook_secret_set
                      ? t("settings.email.passwordKeep")
                      : "whsec_..."
                  }
                  value={form.stripe_settings.webhook_secret}
                  onChange={(e) => set("stripe_settings", "webhook_secret", e.target.value)}
                  data-testid="settings-stripe-webhook"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div>
                  <Label className="cursor-pointer">{t("settings.stripe.enabled")}</Label>
                  {!stripeCanEnable && (
                    <div className="text-xs text-slate-500">{t("settings.stripe.needsKeys")}</div>
                  )}
                </div>
                <Switch
                  checked={form.stripe_settings.enabled}
                  disabled={!stripeCanEnable}
                  onCheckedChange={(v) => set("stripe_settings", "enabled", v)}
                  data-testid="settings-stripe-enabled"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={saveStripeTab}
                disabled={saving}
                data-testid="settings-stripe-save"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("common.save")}
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* BILLING — Platform subscription plans + Stripe Checkout */}
        <TabsContent value="billing" className="space-y-6">
          <BillingTab />
        </TabsContent>

        {/* PAYMENT INFO — Swedish invoicing identifiers */}
        <TabsContent value="payment" className="space-y-6">
          <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
            <div>
              <h2 className="font-heading text-lg">{t("settings.payment.title")}</h2>
              <p className="text-sm text-slate-500">{t("settings.payment.subtitle")}</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.payment.orgNr")}</Label>
                <Input
                  value={form.payment_info.org_nr}
                  onChange={(e) => set("payment_info", "org_nr", e.target.value)}
                  placeholder="559222-4496"
                  data-testid="settings-payment-orgnr"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.payment.vatRegNo")}</Label>
                <Input
                  value={form.payment_info.vat_reg_no}
                  onChange={(e) => set("payment_info", "vat_reg_no", e.target.value)}
                  placeholder="SE559222449601"
                  data-testid="settings-payment-vatregno"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 sm:col-span-2">
                <div>
                  <Label className="cursor-pointer">{t("settings.payment.fSkatt")}</Label>
                  <div className="text-xs text-slate-500">
                    {t("settings.payment.fSkattHint")}
                  </div>
                </div>
                <Switch
                  checked={form.payment_info.f_skatt_approved}
                  onCheckedChange={(v) => set("payment_info", "f_skatt_approved", v)}
                  data-testid="settings-payment-fskatt"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.payment.bankgiro")}</Label>
                <Input
                  value={form.payment_info.bankgiro}
                  onChange={(e) => set("payment_info", "bankgiro", e.target.value)}
                  placeholder="5394-5507"
                  data-testid="settings-payment-bankgiro"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.payment.plusgiro")}</Label>
                <Input
                  value={form.payment_info.plusgiro}
                  onChange={(e) => set("payment_info", "plusgiro", e.target.value)}
                  data-testid="settings-payment-plusgiro"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.payment.swish")}</Label>
                <Input
                  value={form.payment_info.swish_number}
                  onChange={(e) => set("payment_info", "swish_number", e.target.value)}
                  placeholder="1233290327"
                  data-testid="settings-payment-swish"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.payment.iban")}</Label>
                <Input
                  value={form.payment_info.iban}
                  onChange={(e) => set("payment_info", "iban", e.target.value)}
                  placeholder="SE45 5000 0000 0583 9825 7466"
                  data-testid="settings-payment-iban"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.payment.bic")}</Label>
                <Input
                  value={form.payment_info.bic}
                  onChange={(e) => set("payment_info", "bic", e.target.value)}
                  placeholder="SWEDSESS"
                  data-testid="settings-payment-bic"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>{t("settings.payment.interestNote")}</Label>
              {["sv", "en", "ar"].map((lng) => (
                <div key={lng} className="space-y-1">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {lng.toUpperCase()}
                  </div>
                  <Textarea
                    rows={2}
                    dir={lng === "ar" ? "rtl" : "ltr"}
                    value={form.payment_info[`late_payment_interest_note_${lng}`]}
                    onChange={(e) =>
                      set(
                        "payment_info",
                        `late_payment_interest_note_${lng}`,
                        e.target.value
                      )
                    }
                    data-testid={`settings-payment-interest-${lng}`}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={savePaymentTab}
                disabled={saving}
                data-testid="settings-payment-save"
                className="bg-slate-900 text-white hover:bg-slate-800"
              >
                {t("common.save")}
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* ACCOUNTING — Phase 4 */}
        <TabsContent value="accounting" className="space-y-6">
          <AccountingTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AccountingTab() {
  const { t } = useTranslation();
  const [acct, setAcct] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/company/accounting").then(({ data }) => setAcct(data));
  }, []);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/company/accounting", acct);
      toast.success(t("settings.toasts.saved"));
    } catch (err) {
      toast.error(formatApiError(err, t("settings.toasts.error")));
    } finally {
      setSaving(false);
    }
  }

  if (!acct) return <div className="text-sm text-slate-500">{t("common.loading")}</div>;

  const coa = acct.chart_of_accounts || {};
  const byRate = coa.by_tax_rate || {};

  function setCoa(key, val) {
    setAcct({ ...acct, chart_of_accounts: { ...coa, [key]: val } });
  }
  function setRateAccount(rate, field, val) {
    const next = { ...byRate, [rate]: { ...(byRate[rate] || {}), [field]: val } };
    setCoa("by_tax_rate", next);
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-5" data-testid="settings-accounting">
      <div>
        <h2 className="font-heading text-lg">{t("settings.accounting.title")}</h2>
        <p className="text-sm text-slate-500">{t("settings.accounting.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t("settings.accounting.method")}</Label>
          <Select value={acct.method} onValueChange={(v) => setAcct({ ...acct, method: v })}>
            <SelectTrigger data-testid="settings-accounting-method">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="accrual">{t("settings.accounting.methods.accrual")}</SelectItem>
              <SelectItem value="cash">{t("settings.accounting.methods.cash")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("settings.accounting.defaultFormat")}</Label>
          <Select
            value={acct.default_export_format}
            onValueChange={(v) => setAcct({ ...acct, default_export_format: v })}
          >
            <SelectTrigger data-testid="settings-accounting-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="xlsx">XLSX</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("settings.accounting.arAccount")}</Label>
          <Input
            value={coa.ar_account || ""}
            onChange={(e) => setCoa("ar_account", e.target.value)}
            data-testid="settings-accounting-ar"
          />
        </div>
        <div className="space-y-2">
          <Label>{t("settings.accounting.cashAccount")}</Label>
          <Input
            value={coa.cash_account || ""}
            onChange={(e) => setCoa("cash_account", e.target.value)}
            data-testid="settings-accounting-cash"
          />
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">{t("settings.accounting.byRate")}</Label>
        <div className="mt-2 overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("settings.accounting.rate")}</th>
                <th className="px-3 py-2 text-start">{t("settings.accounting.revenue")}</th>
                <th className="px-3 py-2 text-start">{t("settings.accounting.vat")}</th>
              </tr>
            </thead>
            <tbody>
              {["25", "12", "6", "0"].map((rate) => (
                <tr key={rate} className="border-t border-slate-100">
                  <td className="px-3 py-2 tabular-nums">{rate}%</td>
                  <td className="px-3 py-2">
                    <Input
                      value={byRate[rate]?.revenue_account || ""}
                      onChange={(e) => setRateAccount(rate, "revenue_account", e.target.value)}
                      data-testid={`settings-accounting-rev-${rate}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      value={byRate[rate]?.vat_account || ""}
                      onChange={(e) => setRateAccount(rate, "vat_account", e.target.value)}
                      data-testid={`settings-accounting-vat-${rate}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="settings-accounting-save"
          className="bg-slate-900 text-white hover:bg-slate-800"
        >
          {t("common.save")}
        </Button>
      </div>
    </section>
  );
}
